// Ten themes, grouped from minimal → techy. Each theme is just a set of CSS
// custom properties (applied to <html>) plus a preferred map basemap
// ("light" | "dark" | "satellite") so the map matches the mood.
//
// Variables mirror the ones used across styles.css:
//   --bg --panel --panel-2 --ink --muted --accent --accent-strong --match --line

export const THEMES = [
  // ── Minimal ───────────────────────────────────────────
  {
    id: "paper",
    name: "Paper",
    group: "Minimal",
    map: "light",
    vars: {
      "--bg": "#f6f7f9", "--panel": "#ffffff", "--panel-2": "#eef1f5",
      "--ink": "#1a2230", "--muted": "#5b6675", "--accent": "#2f6fed",
      "--accent-strong": "#1b52c4", "--match": "#e08a00", "--line": "#dfe4ea",
    },
  },
  {
    id: "daylight",
    name: "Daylight",
    group: "Minimal",
    map: "light",
    vars: {
      "--bg": "#eaf3fb", "--panel": "#ffffff", "--panel-2": "#dcebf7",
      "--ink": "#0f2942", "--muted": "#4d6a86", "--accent": "#0a84ff",
      "--accent-strong": "#0060df", "--match": "#d97706", "--line": "#c7dcee",
    },
  },
  {
    id: "nordic",
    name: "Nordic",
    group: "Minimal",
    map: "light",
    vars: {
      "--bg": "#eceff4", "--panel": "#ffffff", "--panel-2": "#e5e9f0",
      "--ink": "#2e3440", "--muted": "#4c566a", "--accent": "#5e81ac",
      "--accent-strong": "#4c6a8f", "--match": "#bf616a", "--line": "#d8dee9",
    },
  },
  // ── Classic ───────────────────────────────────────────
  {
    id: "midnight",
    name: "Midnight",
    group: "Classic",
    map: "dark",
    vars: {
      "--bg": "#0b1220", "--panel": "#121b2e", "--panel-2": "#1a2740",
      "--ink": "#eaf0fb", "--muted": "#9fb0cc", "--accent": "#4da3ff",
      "--accent-strong": "#7bc0ff", "--match": "#ffd166", "--line": "#26344f",
    },
  },
  {
    id: "slate",
    name: "Slate",
    group: "Classic",
    map: "dark",
    vars: {
      "--bg": "#15181d", "--panel": "#1e2228", "--panel-2": "#282e37",
      "--ink": "#e7eaef", "--muted": "#9aa2ad", "--accent": "#7aa2f7",
      "--accent-strong": "#a3bbff", "--match": "#e0af68", "--line": "#333b46",
    },
  },
  {
    id: "solarized",
    name: "Solarized",
    group: "Classic",
    map: "dark",
    vars: {
      "--bg": "#002b36", "--panel": "#073642", "--panel-2": "#0a4553",
      "--ink": "#eee8d5", "--muted": "#93a1a1", "--accent": "#268bd2",
      "--accent-strong": "#6cb6e6", "--match": "#b58900", "--line": "#0e5364",
    },
  },
  // ── Vibrant ───────────────────────────────────────────
  {
    id: "sunset",
    name: "Sunset",
    group: "Vibrant",
    map: "dark",
    vars: {
      "--bg": "#1a1020", "--panel": "#271531", "--panel-2": "#361d43",
      "--ink": "#ffeede", "--muted": "#c39bb0", "--accent": "#ff7a59",
      "--accent-strong": "#ffb199", "--match": "#ffd166", "--line": "#4a2a55",
    },
  },
  {
    id: "ocean",
    name: "Ocean",
    group: "Vibrant",
    map: "satellite",
    vars: {
      "--bg": "#041f2e", "--panel": "#062a3d", "--panel-2": "#0a3a52",
      "--ink": "#e2f6ff", "--muted": "#8fc3d8", "--accent": "#12b5cb",
      "--accent-strong": "#5fe0f0", "--match": "#ffd166", "--line": "#0d4a63",
    },
  },
  // ── Techy ─────────────────────────────────────────────
  {
    id: "matrix",
    name: "Matrix",
    group: "Techy",
    map: "dark",
    vars: {
      "--bg": "#000000", "--panel": "#031206", "--panel-2": "#05210c",
      "--ink": "#c8ffcf", "--muted": "#4fbf6a", "--accent": "#00ff66",
      "--accent-strong": "#7cff9f", "--match": "#eaff00", "--line": "#0c3d1a",
    },
  },
  {
    id: "cyberpunk",
    name: "Cyberpunk",
    group: "Techy",
    map: "dark",
    vars: {
      "--bg": "#0a0416", "--panel": "#160a2a", "--panel-2": "#22103f",
      "--ink": "#f4e9ff", "--muted": "#b088e0", "--accent": "#ff2bd6",
      "--accent-strong": "#00e5ff", "--match": "#fde047", "--line": "#3a1d63",
    },
  },
];

const STORAGE_KEY = "themeId";
export const DEFAULT_THEME = "midnight";

export function getTheme(id) {
  return THEMES.find((t) => t.id === id) || THEMES.find((t) => t.id === DEFAULT_THEME);
}

export function applyTheme(id) {
  const theme = getTheme(id);
  const root = document.documentElement;
  for (const [k, v] of Object.entries(theme.vars)) {
    root.style.setProperty(k, v);
  }
  root.dataset.theme = theme.id;
  root.style.setProperty("color-scheme", theme.map === "light" ? "light" : "dark");
  try {
    localStorage.setItem(STORAGE_KEY, theme.id);
  } catch {
    /* ignore */
  }
  return theme;
}

export function loadThemeId() {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}
